/**
 * Site header — front-end behaviour:
 *  1. Mobile menu — a full-screen drawer that slides in from the right.
 *     Its open/closed class lists come from `data-qr-mobile` (JSON, written
 *     by render.php) for the same reason as the scroll states below: no
 *     styling is hardcoded here. Submenu toggle buttons are cloned from the
 *     <template> render.php puts inside the panel, so their markup and
 *     classes stay in PHP too.
 *  2. Transparent + white over a dark hero at the very top of the page;
 *     flips to solid white + dark text once scrolled past it, or
 *     immediately on pages that don't start with a dark section (blog
 *     posts, inner pages, ...). All the class strings for both states
 *     come from `data-qr-header` (JSON, written by render.php) — this
 *     file never hardcodes styling, so editor/render.php stay the source
 *     of truth (see block.json `qrClasses`).
 * The header itself is `position: fixed` (wrapperSolid/wrapperTransparent
 * in block.json) so it stays visible at all times, including on scroll
 * down — it is never hidden or translated off-screen.
 * Loaded only on pages that use the block (viewScript in block.json).
 */
document.querySelectorAll('.qr-site-header').forEach((header) => {
	const toggle = header.querySelector('.qr-menu-toggle');
	const menu = header.querySelector('.qr-mobile-menu');
	const iconOpen = toggle ? toggle.querySelector('[data-qr-part="menuIconOpen"]') : null;
	const iconClose = toggle ? toggle.querySelector('[data-qr-part="menuIconClose"]') : null;

	if (menu) {
		let mobile;
		try {
			mobile = JSON.parse(menu.getAttribute('data-qr-mobile') || '{}');
		} catch (e) {
			mobile = {};
		}

		const tokens = (value) => (value || '').split(/\s+/).filter(Boolean);
		let menuOpen = false;

		const setMenu = (open) => {
			menuOpen = open;
			menu.classList.remove(...tokens(open ? mobile.closed : mobile.open));
			menu.classList.add(...tokens(open ? mobile.open : mobile.closed));

			// The panel covers the whole viewport — stop the page behind it
			// from scrolling along with it.
			document.body.classList.toggle(mobile.bodyLock || 'overflow-hidden', open);

			if (toggle) {
				toggle.setAttribute('aria-expanded', String(open));
			}
			if (iconOpen) {
				iconOpen.classList.toggle('hidden', open);
			}
			if (iconClose) {
				iconClose.classList.toggle('hidden', !open);
			}
		};

		if (toggle) {
			toggle.addEventListener('click', () => setMenu(!menuOpen));
		}

		const closeBtn = menu.querySelector('.qr-mobile-menu-close');
		if (closeBtn) {
			closeBtn.addEventListener('click', () => setMenu(false));
		}

		document.addEventListener('keydown', (event) => {
			if (event.key === 'Escape' && menuOpen) {
				setMenu(false);
			}
		});

		// Same-page anchors don't navigate, so the panel would otherwise stay
		// open on top of the section the visitor just jumped to.
		menu.querySelectorAll('a[href]').forEach((link) => {
			link.addEventListener('click', () => setMenu(false));
		});

		/*
		 * Submenus: wp_nav_menu gives us `li.menu-item-has-children > ul.sub-menu`
		 * and no way to inject a toggle, so one is cloned per parent item from
		 * the template render.php emits. Collapsed on init rather than in PHP so
		 * the submenu links stay reachable if this script never runs.
		 */
		const template = menu.querySelector('[data-qr-part="submenuToggleTemplate"]');

		if (template) {
			menu.querySelectorAll('li.menu-item-has-children').forEach((item) => {
				const sub = item.querySelector(':scope > .sub-menu');
				if (!sub) {
					return;
				}

				sub.classList.add('hidden');

				const btn = template.content.firstElementChild.cloneNode(true);
				const icon = btn.querySelector('span');
				item.insertBefore(btn, sub);

				btn.addEventListener('click', () => {
					const open = sub.classList.toggle('hidden') === false;
					btn.setAttribute('aria-expanded', String(open));
					if (icon && mobile.iconOpen) {
						icon.classList.toggle(mobile.iconOpen, open);
					}
				});
			});
		}
	}

	let state;
	try {
		state = JSON.parse(header.getAttribute('data-qr-header') || '{}');
	} catch (e) {
		state = {};
	}

	const parts = {
		wrapper: header,
		topbarOuter: header.querySelector('[data-qr-part="topbarOuter"]'),
		topbarInner: header.querySelector('[data-qr-part="topbarInner"]'),
		logoFallback: header.querySelector('[data-qr-part="logoFallback"]'),
		navMenu: header.querySelector('[data-qr-part="navMenu"]'),
		menuToggle: header.querySelector('[data-qr-part="menuToggle"]'),
	};
	const logo = header.querySelector('[data-qr-part="logo"]');

	// Solid background kicks in almost immediately on scroll.
	const solidThreshold = 20;

	let ticking = false;
	let isSolid = null; // unknown until first check, forces the initial apply

	/**
	 * Is there a dark (.bg-dark) section directly behind the header right
	 * now? Checked by looking at what's under the header's own bottom edge.
	 */
	const hasDarkHeroBelow = () => {
		const y = header.offsetHeight + 2;
		let el = document.elementFromPoint(window.innerWidth / 2, y);
		let depth = 0;
		while (el && depth < 6) {
			if (el.classList && el.classList.contains('bg-dark')) {
				return true;
			}
			el = el.parentElement;
			depth++;
		}
		return false;
	};

	const applyState = (solid) => {
		if (solid === isSolid) {
			return;
		}
		isSolid = solid;
		const mode = solid ? 'solid' : 'transparent';

		Object.keys(parts).forEach((key) => {
			const el = parts[key];
			const classes = state[key];
			if (el && classes) {
				el.className = classes[mode];
			}
		});

		if (logo && state.logo) {
			logo.src = state.logo[mode];
		}
	};

	const applyScrollState = () => {
		const currentScrollY = window.scrollY;

		if (currentScrollY > solidThreshold) {
			applyState(true);
		} else {
			applyState(!hasDarkHeroBelow());
		}

		ticking = false;
	};

	// Run once on load in case the page opens already scrolled (e.g. a
	// hash link), so the header starts in the correct state.
	applyScrollState();

	window.addEventListener(
		'scroll',
		() => {
			if (!ticking) {
				window.requestAnimationFrame(applyScrollState);
				ticking = true;
			}
		},
		{ passive: true }
	);
});
